import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import createClient from 'openapi-fetch';
import { vendorApi } from '@/features/vendor/api/vendorApi';
import { listPrices, getRule } from '@/features/vendor/api/vendorPricingApi';
import { getPackageDisplayName } from '@/lib/packageDisplay';
import PricingRulePanel from './PricingRulePanel';
import ScenarioSelector from '@/features/super-admin/referral-simulator/components/ScenarioSelector';
import ResultCard from '@/features/super-admin/referral-simulator/components/ResultCard';
import ModelsComparison from '@/features/super-admin/referral-simulator/components/ModelsComparison';
import AlertModal from '@/features/super-admin/referral-simulator/components/AlertModal';
import { Search, X, TrendingUp, TrendingDown, Activity, AlertTriangle, Settings2 } from 'lucide-react';
import '@/features/super-admin/referral-simulator/referral-simulator.css';

const client = createClient({ baseUrl: '/api/v1', credentials: 'include' });

const getVendorSuggestedCaps = async (marginPercent, minMarginPercent = 3) => {
    const params = new URLSearchParams();
    params.set('margin_percent', String(marginPercent));
    params.set('min_margin_percent', String(minMarginPercent));
    const { data, error } = await client.GET(`/vendor/referral/suggested-caps?${params}`);
    if (error) throw new Error(error?.detail || 'خطا در دریافت پیشنهادها');
    return data;
};

const inputStyle = {
    width: '100%', height: '38px', padding: '0 12px', border: '1px solid #d1d5db',
    borderRadius: '6px', boxSizing: 'border-box', fontSize: '0.95rem', textAlign: 'center', outline: 'none',
};

const cellStyle = { padding: '0.75rem 0.5rem', textAlign: 'center', verticalAlign: 'middle' };
const thStyle = { ...cellStyle, fontWeight: 'bold', color: '#374151', whiteSpace: 'nowrap', fontSize: '0.9rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' };

const HintText = ({ text }) => (
    <small style={{ textAlign: 'center', display: 'block', color: '#64748b', marginTop: '0.4rem', fontSize: '0.75rem', lineHeight: '1.4' }}>
        {text}
    </small>
);

const formatNumber = (num) => new Intl.NumberFormat('fa-IR').format(Number(num) || 0);

function EmbeddedSimulatorForm({ onSubmit, onCompare, loading, onValidationError, externalSalesAmount, externalMarginPercent }) {
    const DEFAULTS = { sales_amount: 0, margin_percent: 30, min_margin_percent: 3, max_depth: 5, safety_factor: 50, max_sales_percent: 5, distribution: 'halving' };
    const [values, setValues] = useState(DEFAULTS);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedCap, setSelectedCap] = useState(null);
    const [showManual, setShowManual] = useState(false);
    const [manualValue, setManualValue] = useState('5');
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);

    useEffect(() => { setValues((prev) => ({ ...prev, sales_amount: externalSalesAmount || 0 })); }, [externalSalesAmount]);
    useEffect(() => {
        if (externalMarginPercent !== undefined && externalMarginPercent !== null) {
            setValues((prev) => ({ ...prev, margin_percent: Number(externalMarginPercent).toFixed(1) }));
        }
    }, [externalMarginPercent]);

    useEffect(() => {
        const fetchSuggestions = async () => {
            const margin = Number(values.margin_percent);
            if (!margin || margin <= 0) { setSuggestions([]); return; }
            setSuggestionsLoading(true);
            try {
                const data = await getVendorSuggestedCaps(margin, Number(values.min_margin_percent) || 3);
                setSuggestions(data.suggestions || []);
                if (selectedCap === null && data.suggestions?.length > 0) {
                    const balanced = data.suggestions.find((s) => s.name === 'متعادل');
                    const initial = balanced || data.suggestions[0];
                    setSelectedCap(initial.percent);
                    setValues((prev) => ({ ...prev, max_sales_percent: Number(initial.percent) }));
                }
            } catch (e) { console.error(e); }
            finally { setSuggestionsLoading(false); }
        };
        fetchSuggestions();
    }, [values.margin_percent, values.min_margin_percent]);

    const update = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));
    const handleSelectCap = (percent) => { setSelectedCap(percent); setValues((prev) => ({ ...prev, max_sales_percent: Number(percent) })); };
    const handleToggleManual = () => { if (!showManual) setManualValue(String(selectedCap ?? values.max_sales_percent)); setShowManual((v) => !v); };
    const handleManualChange = (val) => { setManualValue(val); setSelectedCap(null); setValues((prev) => ({ ...prev, max_sales_percent: Number(val) })); };

    const validate = () => {
        const salesAmount = Number(values.sales_amount);
        if (isNaN(salesAmount) || salesAmount <= 0) return 'لطفاً ابتدا کالاها را انتخاب کنید تا مبلغ سبد محاسبه شود.';
        const margin = Number(values.margin_percent);
        if (isNaN(margin) || margin < 0 || margin > 100) return 'حاشیه سود باید بین ۰ تا ۱۰۰ درصد باشد.';
        const minMargin = Number(values.min_margin_percent);
        if (isNaN(minMargin) || minMargin < 0 || minMargin > 100) return 'حداقل حاشیه سود باید بین ۰ تا ۱۰۰ درصد باشد.';
        const depth = Number(values.max_depth);
        if (isNaN(depth) || depth < 1 || depth > 10) return 'حداکثر عمق زنجیره باید بین ۱ تا ۱۰ باشد.';
        const safety = Number(values.safety_factor);
        if (isNaN(safety) || safety <= 0 || safety > 100) return 'سهم پورسانت از سود باید بین ۱ تا ۱۰۰ درصد باشد.';
        return null;
    };

    const buildPayload = () => ({
        ...values, sales_amount: Number(values.sales_amount), margin_percent: Number(values.margin_percent),
        min_margin_percent: Number(values.min_margin_percent), max_depth: Number(values.max_depth),
        safety_factor: Number(values.safety_factor) / 100, max_sales_percent: Number(values.max_sales_percent),
    });

    const handleSubmit = (e) => { e.preventDefault(); const error = validate(); if (error) return onValidationError(error); onSubmit(buildPayload()); };
    const handleCompareClick = () => { const error = validate(); if (error) return onValidationError(error); onCompare(buildPayload()); };

    return (
        <form onSubmit={handleSubmit} className="simulator-form" noValidate style={{ background: '#fff', padding: '1.5rem', borderRadius: '8px', border: '1px solid #bfdbfe', marginTop: '1rem' }}>
            <div className="form-row">
                <div className="form-col"><label><span>مبلغ سبد کالا (پس از تخفیف) (ریال)</span><input type="text" value={formatNumber(values.sales_amount)} disabled style={{ ...inputStyle, backgroundColor: '#f3f4f6', fontWeight: 'bold' }} /><HintText text="مبلغ سبد از کالاهای انتخابی با اعمال تخفیف" /></label></div>
                <div className="form-col"><label><span>حاشیه سود (%)</span><input type="number" value={values.margin_percent} onChange={(e) => update('margin_percent', e.target.value)} step="0.1" style={inputStyle} /><HintText text="درصد سود شما نسبت به پرداخت مشتری" /></label></div>
                <div className="form-col"><label><span>حداقل حاشیه سود (%)</span><input type="number" value={values.min_margin_percent} onChange={(e) => update('min_margin_percent', e.target.value)} step="0.1" style={inputStyle} /><HintText text="حداقل سودی که تحت هر شرایطی باید برای شما باقی بماند" /></label></div>
            </div>
            <div className="form-row">
                <div className="form-col"><label><span>حداکثر عمق زنجیره</span><input type="number" value={values.max_depth} onChange={(e) => update('max_depth', e.target.value)} style={inputStyle} /><HintText text="تعداد نفراتی که در زنجیره معرفی پورسانت می‌گیرند." /></label></div>
                <div className="form-col"><label><span>سهم پورسانت از سود شما (%)</span><input type="number" value={values.safety_factor} onChange={(e) => update('safety_factor', e.target.value)} step="1" min="1" max="100" style={inputStyle} /><HintText text="چند درصد از سود خود را به عنوان پورسانت می‌دهید؟" /></label></div>
                <div className="form-col"><label><span>نحوه توزیع بین سطوح</span><select value={values.distribution} onChange={(e) => update('distribution', e.target.value)} style={inputStyle}><option value="halving">نصف‌شونده</option><option value="linear">خطی</option><option value="gradual">پله‌ای</option></select><HintText text="روش تقسیم پورسانت بین افراد زنجیره." /></label></div>
            </div>
            <ScenarioSelector suggestions={suggestions} selected={selectedCap} onSelect={handleSelectCap} showManual={showManual} onToggleManual={handleToggleManual} manualValue={manualValue} onManualChange={handleManualChange} minMargin={Number(values.min_margin_percent) || 3} />
            {suggestionsLoading && <div className="scenario-loading">در حال محاسبه…</div>}
            <div className="form-actions" style={{ justifyContent: 'center', marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
                <button type="submit" disabled={loading} className="btn-primary" style={{ padding: '0.6rem 2rem', fontSize: '1.05rem' }}>{loading ? 'در حال محاسبه...' : 'شبیه‌سازی'}</button>
                <button type="button" onClick={handleCompareClick} disabled={loading} className="btn-secondary" style={{ padding: '0.6rem 2rem', fontSize: '1.05rem' }}>مقایسه مدل‌ها</button>
            </div>
        </form>
    );
}

const PRICE_KIND = 'representative';

export default function SimulationProductsPage() {
    const [products, setProducts] = useState([]);
    const [editedProducts, setEditedProducts] = useState({});
    const [priceMap, setPriceMap] = useState({});

    const [profiles, setProfiles] = useState([]);
    const [productProfiles, setProductProfiles] = useState({});

    const [ruleChecked, setRuleChecked] = useState(false);
    const [hasRequiredFormulas, setHasRequiredFormulas] = useState(true);

    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const loadPrices = async () => {
        try {
            const rows = await listPrices(PRICE_KIND, false);
            setPriceMap(Object.fromEntries(rows.map((r) => [r.product_id, r])));
        } catch {
            setPriceMap({});
        }
    };

    const loadPricingRule = async () => {
        try {
            const rule = await getRule(PRICE_KIND);
            if (rule && rule.rule_type === 'custom_profiles' && rule.params?.profiles?.length > 0) {
                setProfiles(rule.params.profiles);
                const defaultProfileId = rule.params.profiles[0].id;
                setProductProfiles(prev => ({ ...prev, _default: defaultProfileId }));
                setHasRequiredFormulas(true);
            } else {
                setHasRequiredFormulas(false);
            }
        } catch (e) {
            console.log('فرمول سفارشی یافت نشد.');
            setHasRequiredFormulas(false);
        } finally {
            setRuleChecked(true);
        }
    };

    const [searchQuery, setSearchQuery] = useState('');
    const [isProductListVisible, setIsProductListVisible] = useState(false);

    const [packageName, setPackageName] = useState('');
    const [discountPercent, setDiscountPercent] = useState(0);
    const [items, setItems] = useState({});
    const [isPrivate, setIsPrivate] = useState(false);
    const [accessCode, setAccessCode] = useState('');
    const [maxCapacity, setMaxCapacity] = useState('');

    const [isReferralEnabled, setIsReferralEnabled] = useState(false);
    const [simResult, setSimResult] = useState(null);
    const [editingReferralPercents, setEditingReferralPercents] = useState(null);
    const [comparison, setComparison] = useState(null);
    const [showComparison, setShowComparison] = useState(false);
    const [simLoading, setSimLoading] = useState(false);
    const [compareLoading, setCompareLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMsg, setAlertMsg] = useState('');

    const simDiscountRef = useRef(null);
    const [showSimModal, setShowSimModal] = useState(false);
    const [manualDiscount, setManualDiscount] = useState(0);

    useEffect(() => {
        if (showSimModal) {
            setManualDiscount(discountPercent);
        }
    }, [showSimModal, discountPercent]);

    const loadData = async () => {
        setLoading(true);
        try {
            await loadPricingRule();

            const [prodData, pkgData] = await Promise.all([
                vendorApi.getSimulatorProducts(),
                isEditing ? vendorApi.getPackages() : Promise.resolve([])
            ]);

            setProducts(prodData);

            const defaultProf = productProfiles._default;
            const initialAssignments = {};
            prodData.forEach(p => {
                initialAssignments[p.id] = defaultProf;
            });
            setProductProfiles(prev => ({ ...prev, ...initialAssignments }));

            const initialEdits = {};
            prodData.forEach((p) => {
                initialEdits[p.id] = {
                    is_simulator_eligible: p.is_simulator_eligible || false,
                    cost_price: p.cost_price || '',
                    representative_price: p.representative_price || '',
                    simulation_category: p.simulation_category || '',
                    min_group_quantity: p.min_group_quantity || 1,
                    required_products: p.required_products || [],
                };
            });
            setEditedProducts(initialEdits);

            if (isEditing) {
                const pkg = pkgData.find((p) => p.id === id);
                if (pkg) {
                    setPackageName(getPackageDisplayName(pkg.name));
                    setDiscountPercent(Number(pkg.discount_percent));
                    setMaxCapacity(pkg.max_capacity || '');
                    setIsPrivate(pkg.is_private);
                    setAccessCode(pkg.access_code || '');
                    setIsReferralEnabled(pkg.is_referral_enabled);
                    const newItems = {};
                    pkg.items.forEach((i) => {
                        newItems[i.product_id] = { quantity: i.quantity, required_products: i.required_products || [] };
                    });
                    setItems(newItems);
                    if (pkg.is_referral_enabled && pkg.referral_level_percents) {
                        setEditingReferralPercents(pkg.referral_level_percents);
                    }
                }
            }

            await loadPrices();
        } catch (e) {
            setError(e?.detail || 'خطا در دریافت اطلاعات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [id]);

    const calculateDynamicPrices = (basePrice, profileId) => {
        let prices = { consumer_price: Number(basePrice) || 0, production_price: null, rep_price: null, branch_price: null, cost_price: null };

        const profile = profiles.find(p => p.id === profileId) || profiles[0];
        if (!profile) return prices;

        profile.formulas.forEach(f => {
            if (!f.expression || f.expression.length === 0) return;

            let result = 0;
            let currentOp = null;

            f.expression.forEach(node => {
                if (node.type === 'concept') {
                    const val = prices[node.value] || 0;
                    if (currentOp === null) result = val;
                    else {
                        if (currentOp === 'add') result += val;
                        if (currentOp === 'subtract') result -= val;
                        if (currentOp === 'multiply') result *= val;
                        if (currentOp === 'divide') result = val !== 0 ? result / val : 0;
                    }
                } else if (node.type === 'number') {
                    const val = Number(node.value) || 0;
                    if (currentOp === null) result = val;
                    else {
                        if (currentOp === 'add') result += val;
                        if (currentOp === 'subtract') result -= val;
                        if (currentOp === 'multiply') result *= val;
                        if (currentOp === 'divide') result = val !== 0 ? result / val : 0;
                    }
                } else if (node.type === 'operator') {
                    currentOp = node.value;
                }
            });
            prices[f.target] = Math.round(result);
        });

        if (prices.cost_price === null) prices.cost_price = Math.round(prices.consumer_price * 0.7);
        if (prices.rep_price === null) prices.rep_price = prices.consumer_price;

        return prices;
    };

    const updateQuantity = (productId, qty) => {
        setItems(prev => ({ ...prev, [productId]: { ...prev[productId], quantity: Math.max(1, Number(qty)) } }));
        setSimResult(null);
        setShowComparison(false);
    };

    const generateCode = () => {
        const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
        setAccessCode(`PRIV-${rand}`);
    };

    const filteredProducts = products.filter(p =>
        (p.name_fa || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.name_en || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    let totalBase = 0, totalCost = 0;
    Object.keys(items).forEach(id => {
        const p = products.find(prod => prod.id === id);
        if (p) {
            const qty = Number(items[id].quantity) || 1;
            totalBase += (Number(p.base_price) || 0) * qty;
            const assignedProfile = productProfiles[id] || (profiles.length > 0 ? profiles[0].id : null);
            const dynamicPrices = calculateDynamicPrices(p.base_price, assignedProfile);
            totalCost += (p.cost_price ? Number(p.cost_price) : dynamicPrices.cost_price) * qty;
        }
    });

    const getActiveCommissionPct = () => {
        if (!isReferralEnabled) return 0;
        if (!simResult) return 5;
        let levels = simResult?.levels || simResult?.data?.levels;
        if (Array.isArray(levels) && levels.length > 0) {
            return levels.reduce((sum, l) => sum + (Number(l.percent) || 0), 0);
        } else if (simResult?.total_percent) {
            return Number(simResult.total_percent);
        }
        return 5;
    };

    const activeCommissionPct = getActiveCommissionPct();

    const HIDDEN_MIN_MARGIN = 0.10;
    let maxDiscountPct = 0;
    if (totalBase > 0) {
        const c = activeCommissionPct / 100;
        const denom = totalBase * (1 - HIDDEN_MIN_MARGIN - c);
        const maxD = denom > 0 ? 1 - (totalCost / denom) : 0;
        maxDiscountPct = Math.max(0, Math.min(20, maxD * 100));
    }
    maxDiscountPct = Math.round(maxDiscountPct * 10) / 10;
    const totalDiscounted = totalBase * (1 - discountPercent / 100);
    const actualMarginAmt = totalDiscounted - totalCost;
    const actualMarginPct = totalDiscounted > 0 ? (actualMarginAmt / totalDiscounted) * 100 : 0;

    const isSimStale = !!simResult && simDiscountRef.current !== discountPercent;

    const handleSimulate = async (payload) => {
        setSimLoading(true);
        try {
            const { data, error } = await client.POST('/vendor/referral/simulate', { body: payload });
            if (error) throw new Error(error.detail || 'خطا');
            setSimResult(data);
            simDiscountRef.current = discountPercent;
            setEditingReferralPercents(null);
            setShowComparison(false);
        } catch (e) {
            setAlertMsg(e.message);
            setAlertOpen(true);
            setSimResult(null);
        } finally {
            setSimLoading(false);
        }
    };

    const handleCompare = async (payload) => {
        setCompareLoading(true);
        try {
            const { distribution, custom_weights, ...rest } = payload;
            const { data, error } = await client.POST('/vendor/referral/compare', { body: { ...rest, max_depth: rest.max_depth } });
            if (error) throw new Error(error.detail || 'خطا');
            setComparison(data);
            setShowComparison(true);
        } catch (e) {
            setAlertMsg(e.message);
            setAlertOpen(true);
        } finally {
            setCompareLoading(false);
        }
    };

    const handleSavePackage = async () => {
        if (!packageName.trim()) { setError('نام سبد فروش گروهی الزامی است'); return; }
        if (Object.keys(items).length === 0) { setError('حداقل یک کالا انتخاب کنید'); return; }
        if (isPrivate && !accessCode.trim()) { setError('برای پکیج اختصاصی، کد دسترسی الزامی است'); return; }

        if (Number(discountPercent) > maxDiscountPct) {
            setError('درصد تخفیف وارد شده بیشتر از حداکثر تخفیف ایمن است.');
            return;
        }

        let finalLevelPercents = null;
        if (isReferralEnabled) {
            if (simResult && simResult.levels) {
                finalLevelPercents = {};
                simResult.levels.forEach(l => finalLevelPercents[String(l.level)] = l.percent);
            } else if (editingReferralPercents) {
                finalLevelPercents = editingReferralPercents;
            } else {
                setError('لطفاً ابتدا پورسانت را شبیه‌سازی کنید تا درصدها ذخیره شوند');
                return;
            }
        }

        setSaving(true); setError(''); setSuccessMsg('');
        try {
            const payload = {
                name: packageName, description: null, discount_percent: Number(discountPercent),
                is_referral_enabled: isReferralEnabled, referral_max_depth: simResult ? simResult.levels.length : (editingReferralPercents ? Object.keys(editingReferralPercents).length : 3),
                referral_level_percents: isReferralEnabled ? finalLevelPercents : null,
                is_private: isPrivate, access_code: isPrivate ? accessCode : null, max_capacity: maxCapacity ? Number(maxCapacity) : null, is_active: true,
                items: Object.keys(items).map(id => ({ product_id: id, quantity: items[id].quantity, required_products: items[id].required_products }))
            };

            if (isEditing) {
                await vendorApi.updatePackage(id, payload);
                setSuccessMsg('تغییرات سبد با موفقیت ذخیره شد.');
            } else {
                await vendorApi.createPackage(payload);
                setSuccessMsg('سبد فروش گروهی با موفقیت ایجاد شد.');
            }

            navigate('/vendor/packages');
        } catch (e) {
            setError(e?.detail || 'خطا در ذخیره سبد');
        } finally {
            setSaving(false);
        }
    };

    const getScenarios = () => {
        const calc = (discountPct) => {
            const safeDiscount = Number(discountPct) || 0;
            const buyerPays = totalBase * (1 - safeDiscount / 100);
            const commissionAmt = buyerPays * (activeCommissionPct / 100);
            const profit = buyerPays - totalCost - commissionAmt;
            return { discountPct: safeDiscount, buyerPays, commissionAmt, profit };
        };

        const maxD = maxDiscountPct > 0 ? maxDiscountPct : 10;

        return [
            { name: 'سود زیاد', desc: 'جذب مشتری کمتر، سود بیشتر', ...calc(Math.max(1, maxD * 0.2)), color: '#10b981', bg: '#ecfdf5', icon: <TrendingUp size={24} /> },
            { name: 'سود متعادل', desc: 'تعادل بین جذب مشتری و سود', ...calc(Math.max(2, maxD * 0.5)), color: '#2563eb', bg: '#eff6ff', icon: <Activity size={24} /> },
            { name: 'سود کم', desc: 'جذب حداکثری مشتری، سود کمتر', ...calc(Math.max(3, maxD * 0.8)), color: '#f59e0b', bg: '#fffbeb', icon: <TrendingDown size={24} /> },
        ];
    };

    if (loading || !ruleChecked) return <div style={{ textAlign: 'center', padding: '3rem' }}>در حال دریافت اطلاعات...</div>;

    if (!hasRequiredFormulas) {
        return (
            <div style={{ direction: 'rtl', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', padding: '1rem' }}>
                <div style={{ background: '#fff', padding: '2rem', borderRadius: '12px', border: '2px dashed #f59e0b', textAlign: 'center', maxWidth: '500px' }}>
                    <AlertTriangle size={48} color="#f59e0b" style={{ margin: '0 auto 1rem' }} />
                    <h2 style={{ color: '#92400e', marginBottom: '1rem', fontSize: '1.2rem' }}>مبنای محاسبات تعریف نشده است!</h2>
                    <p style={{ color: '#64748b', lineHeight: '1.6', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                        قبل از اینکه بتوانید سبدکالا را بچینید، لازم است ابتدا فرمول‌های محاسباتی خود (مانند قیمت تولید و قیمت نمایندگی) را در صفحه «مبنای محاسبات» تعریف و ذخیره کنید.
                    </p>
                    <button
                        onClick={() => navigate('/vendor/pricing-rules')}
                        style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}
                    >
                        <Settings2 size={18} /> رفتن به صفحه مبنای محاسبات
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ direction: 'rtl', width: '100%', display: 'flex', flexDirection: 'column' }}>

            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', fontWeight: 'bold' }}>{error}</div>}
            {successMsg && <div style={{ background: '#dcfce7', color: '#15803d', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', fontWeight: 'bold' }}>{successMsg}</div>}

            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1.5rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 250px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 'bold' }}>نام سبد فروش گروهی:</label>
                        <input type="text" placeholder="مثلاً: فروش ویژه بهاره" value={packageName} onChange={e => setPackageName(e.target.value)} style={{ ...inputStyle, textAlign: 'right', border: !packageName.trim() ? '2px solid #ef4444' : '1px solid #d1d5db' }} />
                    </div>
                    <div style={{ flex: '1 1 250px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 'bold' }}>جستجوی کالا:</label>
                        <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0 0.5rem', height: '38px' }}>
                            <Search size={18} color="#94a3b8" style={{ marginLeft: '0.5rem' }} />
                            <input
                                type="text"
                                placeholder="نام کالا..."
                                value={searchQuery}
                                onChange={e => { setSearchQuery(e.target.value); setIsProductListVisible(true); }}
                                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.9rem', background: 'transparent' }}
                            />
                        </div>
                    </div>
                    <div style={{ flex: '0 0 auto', paddingBottom: '0' }}>
                        <button
                            onClick={() => setIsProductListVisible(!isProductListVisible)}
                            style={{ height: '38px', background: isProductListVisible ? '#fef2f2' : '#eff6ff', color: isProductListVisible ? '#ef4444' : '#2563eb', border: isProductListVisible ? '1px solid #fecaca' : '1px solid #bfdbfe', padding: '0 1.5rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', display: 'flex', alignItems: 'center' }}
                        >
                            {isProductListVisible ? 'بستن لیست محصولات' : 'نمایش تمام محصولات'}
                        </button>
                    </div>
                </div>

                {isProductListVisible && (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                            <thead>
                            <tr>
                                <th style={thStyle}>نام کالا</th>
                                {/* 👈 [CHANGED] تغییر نام ستون */}
                                <th style={thStyle}>الگوی محاسباتی</th>
                                <th style={thStyle}>قیمت پایه (ریال)</th>
                                <th style={thStyle}>قیمت تولید (ریال)</th>
                                <th style={thStyle}>قیمت نمایندگی (ریال)</th>
                                <th style={thStyle}>قیمت شعبه (ریال)</th>
                                <th style={thStyle}>قیمت تمام شده (ریال)</th>
                                <th style={thStyle}>تعداد</th>
                                <th style={thStyle}>عملیات</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredProducts.map(p => {
                                const assignedProfileId = productProfiles[p.id] || (profiles.length > 0 ? profiles[0].id : null);
                                const dynPrices = calculateDynamicPrices(p.base_price, assignedProfileId);

                                return (
                                    <tr key={p.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ ...cellStyle, fontWeight: 'bold' }}>{p.name_fa}</td>
                                        <td style={cellStyle}>
                                            <select
                                                value={assignedProfileId || ''}
                                                onChange={(e) => setProductProfiles(prev => ({ ...prev, [p.id]: e.target.value }))}
                                                style={{ padding: '0.2rem', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.75rem', fontWeight: 'bold', color: '#4338ca', background: '#e0e7ff', cursor: 'pointer', outline: 'none' }}
                                            >
                                                {profiles.map(prof => <option key={prof.id} value={prof.id}>{prof.name}</option>)}
                                            </select>
                                        </td>
                                        <td style={cellStyle}>{Number(p.base_price).toLocaleString('fa-IR')}</td>
                                        <td style={{ ...cellStyle, color: '#2563eb' }}>{dynPrices.production_price !== null ? Number(dynPrices.production_price).toLocaleString('fa-IR') : '-'}</td>
                                        <td style={{ ...cellStyle, color: '#10b981', fontWeight: 'bold' }}>{dynPrices.rep_price !== null ? Number(dynPrices.rep_price).toLocaleString('fa-IR') : '-'}</td>
                                        <td style={{ ...cellStyle, color: '#8b5cf6' }}>{dynPrices.branch_price !== null ? Number(dynPrices.branch_price).toLocaleString('fa-IR') : '-'}</td>
                                        <td style={{ ...cellStyle, color: '#ef4444' }}>{dynPrices.cost_price !== null ? Number(dynPrices.cost_price).toLocaleString('fa-IR') : '-'}</td>
                                        <td style={cellStyle}>
                                            <button
                                                onClick={() => {
                                                    setItems(prev => ({ ...prev, [p.id]: { quantity: 1, required_products: [] } }));
                                                    setSimResult(null); setShowComparison(false);
                                                }}
                                                style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '0.4rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                            >
                                                افزودن به سبد
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredProducts.length === 0 && (
                                <tr><td colSpan="9" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>کالایی با این نام یافت نشد.</td></tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                )}

                {Object.keys(items).length > 0 && (
                    <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: '1.5rem' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e293b', fontSize: '1rem' }}>کالاهای انتخاب شده در سبد</h3>
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                <tr>
                                    <th style={thStyle}>نام کالا</th>
                                    {/* 👈 [CHANGED] تغییر نام ستون */}
                                    <th style={thStyle}>الگوی محاسباتی</th>
                                    <th style={thStyle}>قیمت پایه (ریال)</th>
                                    <th style={thStyle}>تعداد در پکیج</th>
                                    <th style={thStyle}>عملیات</th>
                                </tr>
                                </thead>
                                <tbody>
                                {Object.entries(items).map(([id, item]) => {
                                    const p = products.find(prod => prod.id === id);
                                    if (!p) return null;
                                    const assignedProfileId = productProfiles[id] || (profiles.length > 0 ? profiles[0].id : null);
                                    const profileName = profiles.find(pr => pr.id === assignedProfileId)?.name || 'نامشخص';

                                    return (
                                        <tr key={id} style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                                            <td style={{ ...cellStyle, fontWeight: 'bold' }}>{p.name_fa}</td>
                                            <td style={{ ...cellStyle, fontSize: '0.75rem', color: '#4338ca' }}>{profileName}</td>
                                            <td style={cellStyle}>{Number(p.base_price).toLocaleString('fa-IR')}</td>
                                            <td style={cellStyle}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden', width: '100px', margin: '0 auto', background: '#fff' }}>
                                                    <button type="button" onClick={() => updateQuantity(id, item.quantity + 1)} style={{ width: '28px', height: '28px', background: '#ecfdf5', color: '#059669', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}>+</button>
                                                    <input type="number" min="1" value={item.quantity} onChange={e => updateQuantity(id, e.target.value)} style={{ width: '40px', border: 'none', textAlign: 'center', outline: 'none', background: 'transparent', fontWeight: 'bold' }} />
                                                    <button type="button" onClick={() => updateQuantity(id, Math.max(1, item.quantity - 1))} style={{ width: '28px', height: '28px', background: '#fef2f2', color: '#dc2626', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}>−</button>
                                                </div>
                                            </td>
                                            <td style={cellStyle}>
                                                <button onClick={() => { setItems(prev => { const copy = { ...prev }; delete copy[id]; return copy; }); setSimResult(null); setShowComparison(false); }} style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '0.4rem 1rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>حذف</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '3rem', background: '#f8fafc', padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
                                <div style={{ fontSize: '0.95rem' }}>مجموع قیمت پایه: <strong style={{ color: '#3b82f6', fontSize: '1.05rem' }}>{totalBase.toLocaleString('fa-IR')} ریال</strong></div>
                                <div style={{ fontSize: '0.95rem' }}>مجموع بهای تمام شده (برآورد): <strong style={{ color: '#ef4444', fontSize: '1.05rem' }}>{totalCost.toLocaleString('fa-IR')} ریال</strong></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* بقیه کدها دقیقا مثل قبل... */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ background: '#f0fdf4', border: '2px dashed #10b981', padding: '1.25rem', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <input type="checkbox" id="refChk" checked={isReferralEnabled} onChange={e => { setIsReferralEnabled(e.target.checked); setSimResult(null); setEditingReferralPercents(null); setShowComparison(false); }} style={{ width: '1.5rem', height: '1.5rem', cursor: 'pointer', accentColor: '#10b981' }} />
                        <label htmlFor="refChk" style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#065f46', cursor: 'pointer', margin: 0 }}>فعال‌سازی پورسانت معرف برای این پکیج (کسر از سهم فروشنده)</label>
                    </div>

                    {isReferralEnabled && (
                        <div style={{ background: '#eff6ff', padding: '1.5rem', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                            <h4 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#1e40af', textAlign: 'center' }}>تنظیمات زنجیره معرفی</h4>
                            <EmbeddedSimulatorForm onSubmit={handleSimulate} onCompare={handleCompare} loading={simLoading || compareLoading} onValidationError={(msg) => { setAlertMsg(msg); setAlertOpen(true); }} externalSalesAmount={totalDiscounted > 0 ? totalDiscounted : undefined} externalMarginPercent={actualMarginPct > 0 ? actualMarginPct : undefined} />
                            <section className="result-section" style={{ marginTop: '2rem' }}>
                                {!showComparison && simResult && <ResultCard result={simResult} />}
                                {showComparison && comparison && <ModelsComparison data={comparison} />}
                            </section>
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', height: '38px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                    <input type="radio" checked={!isPrivate} onChange={() => setIsPrivate(false)} style={{ width: '1.2rem', height: '1.2rem', accentColor: '#2563eb' }} />
                                    <span style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>پکیج عمومی</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                    <input type="radio" checked={isPrivate} onChange={() => setIsPrivate(true)} style={{ width: '1.2rem', height: '1.2rem', accentColor: '#d97706' }} />
                                    <span style={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#92400e' }}>پکیج اختصاصی</span>
                                </label>
                            </div>

                            {isPrivate && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <label style={{ fontWeight: 'bold', color: '#92400e', fontSize: '0.85rem' }}>کد:</label>
                                    <input type="text" value={accessCode} onChange={e => setAccessCode(e.target.value)} placeholder="PRIV-X8B9C" style={{ ...inputStyle, width: '130px', direction: 'ltr', textAlign: 'center', fontWeight: 'bold', border: '1px solid #f59e0b' }} />
                                    <button onClick={generateCode} style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '0 0.75rem', height: '38px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}>تولید</button>
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontWeight: 'bold', color: '#1e3a8a', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>ظرفیت کل:</label>
                                <input type="number" min="1" placeholder="خالی = نامحدود" value={maxCapacity} onChange={e => setMaxCapacity(e.target.value)} style={{ ...inputStyle, width: '130px', border: '1px solid #60a5fa' }} />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontWeight: 'bold', color: '#1e40af', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>درصد تخفیف تایید شده:</label>
                                <div style={{ ...inputStyle, width: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                    {discountPercent}%
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', alignSelf: 'flex-end' }}>
                        <button onClick={() => setShowSimModal(true)} style={{ background: '#f5f3ff', color: '#7e22ce', border: '1px solid #ddd6fe', padding: '0.6rem 1.5rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Activity size={18} /> تنظیم تخفیف و شبیه‌سازی سود
                        </button>
                        <button onClick={handleSavePackage} disabled={saving} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '0.6rem 2.5rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)' }}>
                            {saving ? 'در حال ذخیره...' : (isEditing ? 'ذخیره تغییرات سبد' : 'ذخیره نهایی پکیج')}
                        </button>
                    </div>
                </div>
            </div>

            {showSimModal && (
                <div onClick={() => setShowSimModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, direction: 'rtl' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: '95%', maxWidth: '1000px', maxHeight: '95vh', overflowY: 'auto', borderRadius: '16px', padding: '1.25rem 1.5rem', position: 'relative' }}>
                        <button onClick={() => setShowSimModal(false)} style={{ position: 'absolute', top: '1.5rem', left: '1.5rem', background: '#fef2f2', border: 'none', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><X size={20} color="#ef4444" /></button>
                        <h2 style={{ marginTop: 0, marginBottom: '0.5rem', fontSize: '1.4rem', color: '#1e293b', textAlign: 'center' }}>تنظیم تخفیف و شبیه‌سازی سود</h2>
                        <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '1rem', fontSize: '0.95rem' }}>یکی از سناریوهای پیشنهادی را انتخاب کنید یا درصد تخفیف دلخواه خود را وارد نمایید.</p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                            {getScenarios().map((scenario, idx) => (
                                <div key={idx} style={{ border: `2px solid ${scenario.color}30`, background: scenario.bg, borderRadius: '12px', padding: '0.9rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: scenario.color }}>{scenario.icon}<div><h3 style={{ margin: 0, fontSize: '1.1rem' }}>{scenario.name}</h3></div></div>
                                    <div style={{ background: '#fff', borderRadius: '8px', padding: '0.6rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}><span style={{ color: '#64748b' }}>درصد تخفیف:</span><strong style={{ color: scenario.color }}>{formatNumber(scenario.discountPct)}%</strong></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}><span style={{ color: '#64748b' }}>پرداختی مشتری:</span><strong>{formatNumber(scenario.buyerPays)}</strong></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}><span style={{ color: '#64748b' }}>پورسانت معرف:</span><strong style={{ color: '#ef4444' }}>{formatNumber(scenario.commissionAmt)}</strong></div>
                                        <div style={{ height: '1px', background: '#e2e8f0', margin: 0 }}></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem' }}><span style={{ fontWeight: 'bold', color: '#1e293b' }}>سود خالص شما:</span><strong style={{ color: scenario.color }}>{formatNumber(scenario.profit)}</strong></div>
                                    </div>
                                    <button onClick={() => { setDiscountPercent(Number(scenario.discountPct.toFixed(1))); setShowSimModal(false); }} style={{ marginTop: 'auto', background: scenario.color, color: '#fff', border: 'none', padding: '0.45rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem' }}>انتخاب این سناریو</button>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '1rem', background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 'bold', color: '#334155' }}>یا درصد تخفیف دلخواه خود را وارد کنید (حداکثر {maxDiscountPct}%):</span>
                            <input type="number" min="0" max={maxDiscountPct} value={manualDiscount} onChange={e => setManualDiscount(e.target.value)} style={{ ...inputStyle, width: '100px', fontSize: '1.1rem', fontWeight: 'bold', color: '#2563eb', border: '2px solid #93c5fd' }} />
                            <button onClick={() => { setDiscountPercent(Number(manualDiscount)); setShowSimModal(false); }} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '0.6rem 1.5rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>اعمال تخفیف دستی</button>
                        </div>
                        {!isReferralEnabled && (<div style={{ marginTop: '0.6rem', background: '#fffbeb', border: '1px solid #fde68a', padding: '0.5rem 1rem', borderRadius: '8px', color: '#b45309', fontSize: '0.85rem', textAlign: 'center' }}>* در حال حاضر پورسانت معرف غیرفعال است. برای محاسبه دقیق پورسانت‌ها، آن را فعال کرده و شبیه‌سازی کنید.</div>)}
                        {isReferralEnabled && !simResult && (<div style={{ marginTop: '0.6rem', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '0.5rem 1rem', borderRadius: '8px', color: '#1e40af', fontSize: '0.85rem', textAlign: 'center' }}>* شما پورسانت را فعال کرده‌اید اما شبیه‌سازی نکرده‌اید. پورسانت‌ها با یک درصد تخمینی (میانگین ۵٪) محاسبه شده‌اند.</div>)}
                        {isSimStale && (<div style={{ marginTop: '0.6rem', background: '#fef3c7', border: '1px solid #fde68a', padding: '0.5rem 1rem', borderRadius: '8px', color: '#92400e', fontSize: '0.85rem', textAlign: 'center' }}>* تخفیف بعد از آخرین شبیه‌سازی تغییر کرده؛ برای پورسانت دقیق دوباره شبیه‌سازی کنید.</div>)}
                    </div>
                </div>
            )}

            <AlertModal open={alertOpen} title="خطا" message={alertMsg} onClose={() => setAlertOpen(false)} />
        </div>
    );
}